import { useState, useEffect, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { handleServiceError } from '@/utils/errors/errorHandler';
import { handleChunkLoadError } from '@/utils/chunkLoadErrorHandler';

interface WindowErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorInfo {
  error: Error;
  timestamp: Date;
  userAgent: string;
  url: string;
}

export const WindowErrorBoundary = ({ children, fallback }: WindowErrorBoundaryProps) => {
  const [hasError, setHasError] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const handleError = async (event: ErrorEvent) => {
      const error = new Error(event.message);
      
      // Check if this is a chunk loading error and handle it automatically
      const wasChunkError = await handleChunkLoadError(error);
      if (wasChunkError) {
        // Page will reload, no need to update state
        return;
      }
      
      const info: ErrorInfo = {
        error,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      setHasError(true);
      setErrorInfo(info);
      
      // Log to audit logs for non-chunk errors
      handleServiceError(error, {
        component: 'WindowErrorBoundary',
        action: 'window_error'
      });
      
      console.error('Error caught by WindowErrorBoundary:', info);
    };

    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      const error = new Error(event.reason?.message || 'Unhandled promise rejection');
      
      // Check if this is a chunk loading error and handle it automatically
      const wasChunkError = await handleChunkLoadError(error);
      if (wasChunkError) {
        // Page will reload, no need to update state
        return;
      }
      
      const info: ErrorInfo = {
        error,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      setHasError(true);
      setErrorInfo(info);
      
      // Log to audit logs for non-chunk errors
      handleServiceError(error, {
        component: 'WindowErrorBoundary',
        action: 'promise_rejection'
      });
      
      console.error('Promise rejection caught by WindowErrorBoundary:', info);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const resetError = () => {
    setHasError(false);
    setErrorInfo(null);
    setRetryCount(prev => prev + 1);
  };

  const goHome = () => {
    window.location.href = '/';
  };

  const reportError = () => {
    if (errorInfo) {
      const errorReport = {
        message: errorInfo.error.message,
        stack: errorInfo.error.stack,
        timestamp: errorInfo.timestamp.toISOString(),
        userAgent: errorInfo.userAgent,
        url: errorInfo.url,
        retryCount
      };
      
      console.log('Error report:', errorReport);
      navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2));
      alert('Error report copied to clipboard');
    }
  };

  if (hasError) {
    return fallback || (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Something went wrong
            </CardTitle>
            <CardDescription>
              We encountered an unexpected error. You can try refreshing the page or return to the home page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {errorInfo && (
              <Alert>
                <Bug className="h-4 w-4" />
                <AlertDescription>
                  <strong>Error:</strong> {errorInfo.error.message}
                  <br />
                  <strong>Time:</strong> {errorInfo.timestamp.toLocaleString()}
                  {retryCount > 0 && (
                    <>
                      <br />
                      <strong>Retry attempts:</strong> {retryCount}
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex flex-wrap gap-2">
              <Button onClick={resetError} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              
              <Button variant="outline" onClick={goHome} className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                Go Home
              </Button>
              
              <Button variant="ghost" onClick={reportError} className="flex items-center gap-2">
                <Bug className="h-4 w-4" />
                Report Error
              </Button>
            </div>
            
            {retryCount > 2 && (
              <Alert>
                <AlertDescription>
                  Multiple retry attempts detected. If the problem persists, please try refreshing your browser or contact support.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
